/**
 * Admin panel: browse expert-audit preference questionnaire submissions.
 *
 * Read-only. Users own their submissions; this panel lets admins search
 * by email, see the answers, and copy them into review notes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Loader2, Search, RefreshCw, Eye, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { adminApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { AdminDateFilter } from './AdminDateFilter';

const ATTRACT_LABELS = {
  recruiters: 'Recruiters',
  prospects_clients: 'Prospects & clients',
  industry_peers: 'Industry peers',
};

const TONE_LABELS = {
  formal: 'Formal',
  authoritative: 'Authoritative',
  conversational: 'Conversational',
  creative: 'Creative',
  approachable: 'Approachable',
};

const ACTION_LABELS = {
  connect: 'Connect',
  message: 'Message',
  visit_website: 'Visit website',
  download_portfolio: 'Download portfolio',
  book_call: 'Book call',
};

function fmtTs(ts) {
  if (!ts) return '—';
  try { return format(new Date(ts), 'MMM d, HH:mm'); } catch (_e) { return ts; }
}

export function AdminExpertPreferencesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [viewing, setViewing] = useState(null);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const reload = async (searchTerm, range = dateRange) => {
    setLoading(true);
    try {
      const params = { q: searchTerm || undefined, limit: 200 };
      if (range?.from) params.date_from = range.from;
      if (range?.to) params.date_to = range.to;
      const r = await adminApi.listExpertPreferences(params);
      setItems(r?.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not load submissions'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const onSearch = (e) => {
    e?.preventDefault?.();
    reload(q);
  };

  const stats = useMemo(() => ({
    total: items.length,
    updated_today: items.filter((r) => {
      if (!r.updated_at) return false;
      try {
        const t = new Date(r.updated_at).getTime();
        return Date.now() - t < 24 * 60 * 60 * 1000;
      } catch (_e) { return false; }
    }).length,
  }), [items]);

  return (
    <Card className="border-border" data-testid="admin-expert-prefs-panel">
      <CardContent className="p-0">
        <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
          <ClipboardList size={18} className="text-primary" />
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold">Expert-audit preference questionnaires</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Users&rsquo; answers on who to attract, tone, skills, stories and CTAs &mdash; feeds the rewrite.
              Newest first.
            </p>
          </div>
          <Badge variant="outline" className="font-mono-edge text-[10px]">
            {stats.total} total · {stats.updated_today} today
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => reload(q)}
            disabled={loading}
            data-testid="admin-prefs-refresh-btn"
          >
            {loading ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <RefreshCw size={12} className="mr-1.5" />}
            Refresh
          </Button>
        </div>

        <form onSubmit={onSearch} className="p-4 border-b border-border flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by user email"
              className="pl-7"
              data-testid="admin-prefs-search-input"
            />
          </div>
          <Button type="submit" size="sm" disabled={loading} data-testid="admin-prefs-search-btn">Search</Button>
          <AdminDateFilter
            value={dateRange}
            onChange={(range) => { setDateRange(range); reload(q, range); }}
            testId="admin-prefs-date-filter"
          />
        </form>

        {loading && items.length === 0 ? (
          <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center" data-testid="admin-prefs-empty">
            <ClipboardList size={22} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No preference submissions yet.
            </p>
          </div>
        ) : (
          <Table data-testid="admin-prefs-table">
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Attract</TableHead>
                <TableHead>Tone</TableHead>
                <TableHead className="text-right">Skills</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.user_id} data-testid={`admin-prefs-row-${r.user_id}`}>
                  <TableCell className="text-xs">
                    <div className="font-medium truncate max-w-[220px]" title={r.email}>{r.email || '—'}</div>
                    {r.name && <div className="text-[10px] text-muted-foreground truncate max-w-[220px]">{r.name}</div>}
                  </TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="secondary" className="text-[10px] font-mono-edge">
                      {ATTRACT_LABELS[r.answers?.attract_audience] || r.answers?.attract_audience || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    <Badge variant="outline" className="text-[10px] font-mono-edge">
                      {TONE_LABELS[r.answers?.tone] || r.answers?.tone || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono-edge text-xs">
                    {(r.answers?.top_skills || []).length}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{fmtTs(r.updated_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setViewing(r)}
                      data-testid={`admin-prefs-view-${r.user_id}`}
                    >
                      <Eye size={11} className="mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {viewing && (
        <ViewSubmissionDialog row={viewing} onClose={() => setViewing(null)} />
      )}
    </Card>
  );
}

function ViewSubmissionDialog({ row, onClose }) {
  const a = row.answers || {};
  const skills = a.top_skills || [];
  const actions = a.visitor_actions || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-prefs-view-dialog">
        <DialogHeader>
          <DialogTitle className="truncate">Preferences · {row.email}</DialogTitle>
          <DialogDescription>
            Submitted {fmtTs(row.submitted_at)} · Last updated {fmtTs(row.updated_at)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <Field label="Q1 · Attract">
            <Badge variant="secondary" className="font-mono-edge text-[10px]">
              {ATTRACT_LABELS[a.attract_audience] || a.attract_audience || '—'}
            </Badge>
          </Field>
          <Field label="Q2 · Targeting">
            <p className="text-sm whitespace-pre-wrap">{a.target_industries_titles || '—'}</p>
          </Field>
          <Field label="Q3 · Skills">
            {skills.length ? (
              <div className="flex flex-wrap gap-1.5">
                {skills.map((s, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] font-mono-edge">{s}</Badge>
                ))}
              </div>
            ) : <span className="text-muted-foreground">—</span>}
          </Field>
          <Field label="Q4 · Success stories">
            <p className="text-sm whitespace-pre-wrap">{a.success_stories || <span className="text-muted-foreground">— (optional, not provided)</span>}</p>
          </Field>
          <Field label="Q5 · Problems solved">
            <p className="text-sm whitespace-pre-wrap">{a.problems_solved || '—'}</p>
          </Field>
          <Field label="Q6 · Tone">
            <Badge variant="secondary" className="font-mono-edge text-[10px]">
              {TONE_LABELS[a.tone] || a.tone || '—'}
            </Badge>
          </Field>
          <Field label="Q7 · Desired visitor actions">
            {actions.length ? (
              <div className="flex flex-wrap gap-1.5">
                {actions.map((k) => (
                  <Badge key={k} variant="outline" className="text-[10px] font-mono-edge">
                    {ACTION_LABELS[k] || k}
                  </Badge>
                ))}
              </div>
            ) : <span className="text-muted-foreground">— (none selected)</span>}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            <X size={14} className="mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="border-b border-border pb-3 last:border-0">
      <p className="text-[10px] font-mono-edge uppercase tracking-[0.14em] text-primary mb-1.5">{label}</p>
      {children}
    </div>
  );
}

export default AdminExpertPreferencesPanel;
